const ABOUT_AUTH_TOKEN_KEY = "vh_about_ai_token";
const ABOUT_SESSION_ACTIVE_KEY = "vh_about_ai_session_active";
const ABOUT_ACCESS_STATUS_KEY = "vh_about_ai_access_status";
const ABOUT_AUTH_FLAG =
  String(
    process.env.REACT_APP_VH_ABOUT_AUTH_ENABLE ||
      process.env.VITE_VH_ABOUT_AUTH_ENABLE ||
      "0",
  )
    .trim()
    .toLowerCase() in { 1: true, true: true, yes: true, on: true };

const API_BASE_URL =
  process.env.REACT_APP_API_BASE || "https://api.universevh.org";

export function isAboutAuthEnabled() {
  return ABOUT_AUTH_FLAG;
}

export function getAboutAuthToken() {
  try {
    return String(sessionStorage.getItem(ABOUT_AUTH_TOKEN_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function setAboutAuthToken(token) {
  const value = String(token || "").trim();
  if (!value) return;
  try {
    sessionStorage.setItem(ABOUT_AUTH_TOKEN_KEY, value);
  } catch {}
}

export function clearAboutAuthToken() {
  try {
    sessionStorage.removeItem(ABOUT_AUTH_TOKEN_KEY);
  } catch {}
}

export function isAboutSessionActive() {
  try {
    return sessionStorage.getItem(ABOUT_SESSION_ACTIVE_KEY) === "1";
  } catch {
    return false;
  }
}

function setAboutSessionActive(active) {
  try {
    if (active) sessionStorage.setItem(ABOUT_SESSION_ACTIVE_KEY, "1");
    else sessionStorage.removeItem(ABOUT_SESSION_ACTIVE_KEY);
  } catch {}
}

function normalizeAboutAccessStatus(payload = {}) {
  const accessState = String(payload?.access_state || "").trim().toLowerCase();
  const canUseAiPremium =
    payload?.can_use_ai_premium === true ||
    Boolean(payload?.capabilities?.can_use_ai_premium) ||
    accessState === "premium_full" ||
    accessState === "premium_onboarding";
  return {
    authenticated: payload?.authenticated === true || payload?.ok === true || canUseAiPremium,
    access_state: accessState,
    can_use_ai_premium: canUseAiPremium,
  };
}

function decodeAboutTokenPayload(token) {
  const raw = String(token || "").trim();
  if (!raw) return {};
  const [encodedPayload] = raw.split(".");
  if (!encodedPayload) return {};
  try {
    const normalized = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4 || 4)) % 4);
    const decoded = atob(padded);
    const payload = JSON.parse(decoded);
    return normalizeAboutAccessStatus({
      authenticated: true,
      access_state: payload?.access_state,
      can_use_ai_premium: payload?.can_use_ai_premium,
    });
  } catch {
    return {};
  }
}

export function setAboutAccessStatus(payload = {}) {
  const status = normalizeAboutAccessStatus(payload);
  try {
    sessionStorage.setItem(ABOUT_ACCESS_STATUS_KEY, JSON.stringify(status));
  } catch {}
  return status;
}

export function getAboutAccessStatus() {
  try {
    const raw = sessionStorage.getItem(ABOUT_ACCESS_STATUS_KEY) || "";
    if (!raw) return normalizeAboutAccessStatus({});
    return normalizeAboutAccessStatus(JSON.parse(raw));
  } catch {
    return normalizeAboutAccessStatus({});
  }
}

export function hasAboutFullAiAccess() {
  return Boolean(getAboutAccessStatus().can_use_ai_premium);
}

export function consumeAboutTokenFromUrl() {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  const token = String(url.searchParams.get("vh_token") || "").trim();
  if (!token) return "";
  setAboutAuthToken(token);
  setAboutSessionActive(false);
  setAboutAccessStatus(decodeAboutTokenPayload(token));
  url.searchParams.delete("vh_token");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", next || "/");
  return token;
}

export function consumeAboutCodeFromUrl() {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  const code = String(url.searchParams.get("vh_code") || "").trim();
  if (!code) return "";
  url.searchParams.delete("vh_code");
  const next = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", next || "/");
  return code;
}

export async function exchangeAboutHandoff(code) {
  const normalized = String(code || "").trim();
  if (!normalized) return { ok: false, code: "code_missing" };
  try {
    const response = await fetch(`${API_BASE_URL}/auth/about/exchange`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code: normalized }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || !payload?.ok) {
      return { ok: false, code: payload?.code || "exchange_failed" };
    }
    setAboutSessionActive(true);
    setAboutAccessStatus(payload);
    return { ok: true, ...payload };
  } catch {
    return { ok: false, code: "exchange_unreachable" };
  }
}

export async function bootstrapAboutAuthFromUrl() {
  const legacyToken = consumeAboutTokenFromUrl();
  const code = consumeAboutCodeFromUrl();
  if (code) {
    const result = await exchangeAboutHandoff(code);
    if (result?.ok) return result;
    if (legacyToken) {
      return { ok: true, legacy: true, fallback: true };
    }
    return result;
  }
  return { ok: true, legacy: Boolean(legacyToken) };
}

export async function fetchAboutAuthStatus() {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/about/status`, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) return { authenticated: false, can_use_ai_premium: false };
    const payload = await response.json();
    if (payload?.authenticated) setAboutSessionActive(true);
    else setAboutSessionActive(false);
    setAboutAccessStatus(payload);
    return payload;
  } catch {
    return { authenticated: false, can_use_ai_premium: false };
  }
}
